export interface DeviceInfoPayload {
  deviceId: string;
  deviceName: string;
  deviceType: 'desktop' | 'mobile' | 'tablet' | 'web';
  osInfo: string;
  browserInfo: string;
  fingerprint: string;
}

const DEVICE_ID_KEY = 'mmo_device_id';

/**
 * Returns or generates a persistent device UUID in localStorage.
 */
export function getDeviceId(): string {
  if (typeof window === 'undefined') return 'server-env';
  let deviceId = localStorage.getItem(DEVICE_ID_KEY);
  if (!deviceId) {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      deviceId = crypto.randomUUID();
    } else {
      deviceId = 'dev-' + Math.random().toString(36).substring(2, 15) + Date.now().toString(36);
    }
    localStorage.setItem(DEVICE_ID_KEY, deviceId);
  }
  return deviceId;
}

/**
 * Parses User-Agent for OS information.
 */
export function getOSInfo(): string {
  if (typeof navigator === 'undefined') return 'Unknown OS';
  const ua = navigator.userAgent;
  if (/iphone|ipad|ipod/i.test(ua)) return 'iOS';
  if (/macintosh|mac os x/i.test(ua)) {
    if (typeof navigator !== 'undefined' && navigator.maxTouchPoints && navigator.maxTouchPoints > 1) {
      return 'iOS';
    }
    return 'macOS';
  }
  if (/windows|win32|win64/i.test(ua)) return 'Windows';
  if (/android/i.test(ua)) return 'Android';
  if (/linux/i.test(ua)) return 'Linux';
  return 'Unknown OS';
}

/**
 * Parses User-Agent for Browser information.
 */
export function getBrowserInfo(): string {
  if (typeof navigator === 'undefined') return 'Web Browser';
  const ua = navigator.userAgent;
  if (/edg/i.test(ua)) return 'Edge';
  if (/chrome|crios/i.test(ua) && !/edg/i.test(ua)) return 'Chrome';
  if (/safari/i.test(ua) && !/chrome|crios/i.test(ua)) return 'Safari';
  if (/firefox|fxios/i.test(ua)) return 'Firefox';
  if (/opera|opr/i.test(ua)) return 'Opera';
  return 'Web Browser';
}

/**
 * Detects device type.
 */
export function getDeviceType(): 'desktop' | 'mobile' | 'tablet' | 'web' {
  if (typeof navigator === 'undefined') return 'web';
  const ua = navigator.userAgent;
  if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) {
    return 'tablet';
  }
  if (typeof navigator !== 'undefined' && /macintosh/i.test(ua) && navigator.maxTouchPoints && navigator.maxTouchPoints > 1) {
    return 'tablet';
  }
  if (/Mobile|iP(hone|od)|Android|BlackBerry|IEMobile|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(ua)) {
    return 'mobile';
  }
  return 'desktop';
}

/**
 * Returns a human-friendly device name (e.g. "Chrome on macOS" or "Mobile Safari on iOS").
 */
export function getDeviceName(): string {
  const browser = getBrowserInfo();
  const os = getOSInfo();
  const type = getDeviceType();
  const typeLabel = type === 'mobile' ? 'Mobile ' : type === 'tablet' ? 'Tablet ' : '';
  return `${typeLabel}${browser} on ${os}`;
}

/**
 * Simple hash function for fingerprinting
 */
function simpleHash(str: string): string {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash |= 0;
  }
  return Math.abs(hash).toString(16);
}

/**
 * Generates a lightweight Canvas + Navigator fingerprint hash.
 */
export function getDeviceFingerprint(): string {
  if (typeof window === 'undefined') return 'server';
  try {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    let canvasData = '';
    if (ctx) {
      canvas.width = 200;
      canvas.height = 40;
      ctx.textBaseline = 'top';
      ctx.font = '14px Arial';
      ctx.fillStyle = '#f60';
      ctx.fillRect(125, 1, 62, 20);
      ctx.fillStyle = '#069';
      ctx.fillText('MarketMacroOptix', 2, 15);
      canvasData = canvas.toDataURL();
    }
    const components = [
      navigator.userAgent,
      navigator.language,
      screen.width + 'x' + screen.height,
      screen.colorDepth,
      new Date().getTimezoneOffset(),
      canvasData
    ];
    return simpleHash(components.join('||'));
  } catch (e) {
    return simpleHash(navigator.userAgent + (navigator.language || ''));
  }
}

/**
 * Returns complete payload for API header / body
 */
export function getDeviceInfoPayload(): DeviceInfoPayload {
  return {
    deviceId: getDeviceId(),
    deviceName: getDeviceName(),
    deviceType: getDeviceType(),
    osInfo: getOSInfo(),
    browserInfo: getBrowserInfo(),
    fingerprint: getDeviceFingerprint(),
  };
}
